import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    CustomAuthPublicClientApplication,
    type ICustomAuthPublicClientApplication,
    SignInPasswordRequiredState,
    type PopupRequest,
} from '@azure/msal-browser/custom-auth';
import { customAuthConfig } from '../../nativeAuthConfig';
import { authStyles, sanitizeInput, isValidEmail } from './authStyles';
import { NATIVE_AUTH_SESSION_KEY } from '../../components/LogoutButton';
import { completeNativeAuthSession } from '../../utils/nativeAuthComplete';

export default function LoginPage() {
    const navigate = useNavigate();
    const [authClient, setAuthClient] = useState<ICustomAuthPublicClientApplication | null>(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [focusedField, setFocusedField] = useState<string | null>(null);

    // Initialize native auth client
    useEffect(() => {
        const init = async () => {
            try {
                const instance = await CustomAuthPublicClientApplication.create(customAuthConfig);
                setAuthClient(instance);

                const account = instance.getCurrentAccount();
                if (account.isCompleted()) {
                    window.location.href = '/';
                    return;
                }
            } catch {
                setError('Nepodarilo sa pripojiť k autentifikačnej službe. Skúste obnoviť stránku.');
            }
        };
        init();
    }, [navigate]);

    const handleEmailBlur = () => {
        setFocusedField(null);
        const cleanEmail = sanitizeInput(email.trim().toLowerCase(), 254);
        if (cleanEmail && !isValidEmail(cleanEmail)) {
            setEmailError('E-mailová adresa nie je v správnom tvare.');
        }
    };

    // Single submit: runs the full initiate → challenge → submitPassword flow internally
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const cleanEmail = sanitizeInput(email.trim().toLowerCase(), 254);

        if (!cleanEmail) { setEmailError('Zadajte e-mailovú adresu.'); return; }
        if (!isValidEmail(cleanEmail)) { setEmailError('E-mailová adresa nie je v správnom tvare.'); return; }
        if (emailError) return;
        if (!password) { setError('Zadajte heslo.'); return; }
        if (!authClient) return;

        setLoading(true);

        try {
            // Step 1: initiate sign-in with email
            const signInResult = await authClient.signIn({ username: cleanEmail });

            if (signInResult.isFailed()) {
                if (signInResult.error?.isUserNotFound() || signInResult.error?.isInvalidUsername()) {
                    // Don't reveal whether email exists — show generic error
                    await fakeDelay();
                    setError('Nesprávny e-mail alebo heslo.');
                } else if ((signInResult.error as any)?.isRedirectRequired?.()) {
                    setError('Pre toto konto je potrebné prihlásiť sa cez Google.');
                } else {
                    setError(getErrorMessage(signInResult.error));
                }
                setLoading(false);
                return;
            }

            if (signInResult.isCompleted()) {
                await completeNativeAuthSession(cleanEmail);
                return;
            }

            // Step 2: submit password (challenge step)
            if (!(signInResult.state instanceof SignInPasswordRequiredState)) {
                setError('Nastala neočakávaná chyba. Skúste to znova.');
                setLoading(false);
                return;
            }

            const passwordResult = await signInResult.state.submitPassword(password);

            if (passwordResult.isFailed()) {
                setError('Nesprávny e-mail alebo heslo.');
                setLoading(false);
                return;
            }

            if (passwordResult.isCompleted()) {
                await completeNativeAuthSession(cleanEmail);
                return;
            }

            setError('Nastala neočakávaná chyba. Skúste to znova.');
        } catch {
            setError('Nastala chyba pri prihlasovaní. Skúste to znova.');
        }

        setLoading(false);
    };

    // Google login via browser-delegated flow (popup)
    const handleGoogleLogin = async () => {
        if (!authClient) return;
        setError('');
        setLoading(true);

        try {
            const popUpRequest: PopupRequest = {
                authority: customAuthConfig.auth.authority,
                scopes: [],
                redirectUri: customAuthConfig.auth.redirectUri || '/',
                prompt: 'login',
            };

            await authClient.loginPopup(popUpRequest);

            const accountResult = authClient.getCurrentAccount();
            if (accountResult.isCompleted()) {
                localStorage.setItem(NATIVE_AUTH_SESSION_KEY, 'true');
                window.location.href = '/';
                return;
            }

            setError('Prihlásenie cez Google zlyhalo. Skúste to znova.');
        } catch (err: any) {
            if (err?.errorCode !== 'user_cancelled') {
                setError('Prihlásenie cez Google zlyhalo. Skúste to znova.');
            }
        }
        setLoading(false);
    };

    const fakeDelay = () => new Promise(r => setTimeout(r, 800 + Math.random() * 400));

    const getErrorMessage = (error: any): string => {
        const desc = error?.errorData?.errorDescription;
        if (desc?.includes('locked')) return 'Účet je dočasne zablokovaný. Skúste to neskôr.';
        if (desc?.includes('throttl') || desc?.includes('rate')) return 'Príliš veľa pokusov. Počkajte minútu a skúste znova.';
        return 'Nastala neočakávaná chyba. Skúste to znova.';
    };

    const getInputStyle = (field: string, hasError: boolean = false) => ({
        ...authStyles.input,
        ...(hasError ? authStyles.inputError : {}),
        ...(focusedField === field && !hasError ? authStyles.inputFocused : {}),
    });

    return (
        <div style={authStyles.page}>
            <div style={authStyles.card}>
                <div style={authStyles.logo}>
                    <div style={authStyles.logoIcon}>📄</div>
                    <h1 style={authStyles.logoTitle}>SmartBloček</h1>
                    <p style={authStyles.subtitle}>Prihlásenie do účtu</p>
                </div>

                <form onSubmit={handleSubmit} style={authStyles.form}>
                    <div style={authStyles.inputGroup}>
                        <label style={authStyles.label} htmlFor="email">E-mail</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(sanitizeInput(e.target.value, 254));
                                setEmailError('');
                            }}
                            onFocus={() => setFocusedField('email')}
                            onBlur={handleEmailBlur}
                            style={getInputStyle('email', !!emailError)}
                            placeholder="vas@email.sk"
                            autoFocus
                            autoComplete="email"
                            maxLength={254}
                        />
                        {emailError && <span style={authStyles.fieldError}>{emailError}</span>}
                    </div>

                    <div style={authStyles.inputGroup}>
                        <label style={authStyles.label} htmlFor="password">Heslo</label>
                        <div style={authStyles.passwordWrapper as any}>
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value.slice(0, 128))}
                                onFocus={() => setFocusedField('password')}
                                onBlur={() => setFocusedField(null)}
                                style={getInputStyle('password')}
                                placeholder="Zadajte heslo"
                                autoComplete="current-password"
                                maxLength={128}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={authStyles.passwordToggle as any}
                                tabIndex={-1}
                                aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                            >
                                {showPassword ? 'Skryť' : 'Ukázať'}
                            </button>
                        </div>
                        <div style={{ textAlign: 'right', marginTop: '6px' }}>
                            <Link to="/auth/reset-password" style={{ ...authStyles.link, fontSize: '13px' }}>
                                Zabudli ste heslo?
                            </Link>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !authClient || !!emailError}
                        style={{
                            ...authStyles.button,
                            ...(loading || !authClient || !!emailError ? authStyles.buttonDisabled : {}),
                        }}
                    >
                        {loading ? 'Prihlasujem...' : !authClient ? 'Pripájam sa...' : 'Prihlásiť sa'}
                    </button>

                    <div style={authStyles.divider}>
                        <div style={authStyles.dividerLine} />
                        <span style={authStyles.dividerText}>alebo</span>
                        <div style={authStyles.dividerLine} />
                    </div>

                    <button
                        type="button"
                        style={authStyles.googleButton}
                        disabled={loading || !authClient}
                        onClick={handleGoogleLogin}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
                            <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                        </svg>
                        Prihlásiť sa cez Google
                    </button>
                </form>

                {error && (
                    <div style={{ ...authStyles.error, marginTop: '16px' }}>
                        {error}
                    </div>
                )}

                <div style={authStyles.footer as any}>
                    Nemáte účet?{' '}
                    <Link to="/auth/register" style={authStyles.link}>
                        Zaregistrujte sa
                    </Link>
                </div>
            </div>
        </div>
    );
}
